/**
 * FilePreview
 *
 * Resolves a private S3 key to a short-lived presigned URL via
 * `useRestoredDocumentFile` and delegates rendering to the appropriate
 * read-only display component:
 *
 *  - image/* (JPEG, PNG, GIF, WEBP, HEIC, SVG) → ImageUpload (readOnly)
 *  - all other types (PDF, unknown, …)          → FileUpload (readOnly) +
 *                                                  "View document" opens URL
 *
 * Loading and error lifecycle is driven by the hook's `uploadState`.
 */

import React, { memo, useCallback } from "react";
import { Linking, View } from "react-native";

import { useRestoredDocumentFile } from "@/hooks/useRestoredDocumentFile";
import { LoadingState } from "@/components/ui/LoadingState";
import { ErrorState } from "@/components/ui/ErrorState";
import { FileUpload, type SelectedFile } from "@/components/ui/FileUpload";
import { ImageUpload } from "@/components/ui/ImageUpload";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";

// ─── MIME helpers ─────────────────────────────────────────────────────────────

const IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/svg+xml",
]);

const EXT_TO_MIME: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  svg: "image/svg+xml",
};

export type FilePreviewMimeCategory = "image" | "pdf" | "unsupported";

function inferMimeType(key: string): string {
  const ext = key.split(".").pop()?.toLowerCase() ?? "";
  return EXT_TO_MIME[ext] ?? "application/octet-stream";
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FilePreviewProps {
  /**
   * The S3 object key returned by the document upload endpoint, e.g.
   * "pre-vetting/documents/<vendor-id>/<uuid>.pdf"
   */
  fileKey: string;
  /**
   * MIME type override.  When omitted the type is inferred from the key's
   * file extension.  Pass this when the extension is missing or ambiguous.
   */
  contentType?: string;
  /**
   * Human-readable filename used for accessibility labels.
   * When omitted, the last path segment of `fileKey` is used.
   */
  filename?: string;
  /** Additional Tailwind classes for the loading / error wrapper. */
  className?: string;
  testID?: string;
  isVendor?: boolean;
  applicationId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FilePreview = memo(function FilePreview({
  fileKey,
  contentType,
  filename,
  className,
  testID,
  isVendor = true,
  applicationId,
}: FilePreviewProps) {
  const resolvedFilename = filename ?? fileKey.split("/").pop() ?? fileKey;
  const resolvedMime = contentType ?? inferMimeType(fileKey);
  const isImage = IMAGE_TYPES.has(resolvedMime);

  // ─── Fetch presigned URL ──────────────────────────────────────────────────
  const { accessUrl, uploadState } = useRestoredDocumentFile(fileKey, isVendor, applicationId);

  // ─── Open in browser (FileUpload readOnly "View document" handler) ────────
  const handleView = useCallback(() => {
    if (accessUrl) Linking.openURL(accessUrl);
  }, [accessUrl]);

  // ─── Render: loading ──────────────────────────────────────────────────────
  if (uploadState === "idle" || uploadState === "uploading") {
    return (
      <View
        testID={testID}
        className={cn(
          "flex-1 justify-center items-center bg-white border border-stroke-50 rounded-lg",
          className
        )}
      >
        <LoadingState label="filePreview.loading" mode="default" />
      </View>
    );
  }

  // ─── Render: error ────────────────────────────────────────────────────────
  if (uploadState === "error") {
    return (
      <View
        testID={testID}
        className={cn(
          "flex-1 justify-center items-center bg-white border border-stroke-50 rounded-lg",
          className
        )}
      >
        <ErrorState
          title="filePreview.error"
          description="filePreview.errorDescription"
          testID={`${testID ?? "file-preview"}-error`}
          className="py-6"
          icon={
            <View className="bg-error-light rounded-full p-4">
              <Icon as={Feather} name="x" size={24} className="text-error" />
            </View>
          }
        />
      </View>
    );
  }

  // ─── Render: image ────────────────────────────────────────────────────────
  if (isImage) {
    return (
      <ImageUpload
        readOnly
        currentUris={[accessUrl!]}
        onImages={() => {}}
        uploadState="success"
        testID={testID}
        containerClassName={className}
      />
    );
  }

  // ─── Render: PDF / unsupported ────────────────────────────────────────────
  const currentFile: SelectedFile = {
    uri: accessUrl!,
    name: resolvedFilename,
    type: resolvedMime,
  };

  return (
    <FileUpload
      readOnly
      currentFile={currentFile}
      onFile={() => {}}
      uploadState="success"
      onViewFile={handleView}
      testID={testID}
      containerClassName={className}
    />
  );
});
